import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Typography, Box, Paper, Button, Alert, Divider } from '@mui/material';
import { CheckCircle, Error, Sync, Payment, Receipt, Home } from '@mui/icons-material';
import { checkPaymentStatus, confirmPayment } from '../../utils/paymentService';
import { db } from '../../firebase/config';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import PaymentStatusDisplay from './PaymentStatusDisplay';
import { motion } from 'framer-motion';

// CSS for rotating icons
const rotatingIconStyle = {
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  },
  animation: 'spin 2s linear infinite'
};

const PaymentProcessing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const { currentUser } = useAuth();
  const [activeStep, setActiveStep] = useState(1);
  const [paymentStatus, setPaymentStatus] = useState('processing');
  const [orderReference, setOrderReference] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [orderDetails, setOrderDetails] = useState(null);
  const [progress, setProgress] = useState(0);
  // Use useRef for tracking mounted state
  const isMounted = useRef(true);

  // Get order reference from URL query params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const reference = queryParams.get('reference') || queryParams.get('ref') || queryParams.get('order_id');
    const txnId = queryParams.get('transaction_id');
    
    if (reference) {
      setOrderReference(reference);
      setActiveStep(1);
    } else {
      setError('No order reference found');
      setPaymentStatus('error');
    }
    
    if (txnId) {
      setTransactionId(txnId);
    }
  }, [location.search]);
  
  // Fetch order details from Firestore with sessionStorage fallback
  useEffect(() => {
    let isMounted = true;
    
    const fetchOrderDetails = async () => {
      if (!orderReference) return;
      
      try {
        // First check sessionStorage for payment status
        try {
          const storedPaymentStatus = sessionStorage.getItem(`payment_status_${orderReference}`);
          if (storedPaymentStatus) {
            const statusData = JSON.parse(storedPaymentStatus);
            console.log('Found payment status in sessionStorage:', statusData);
            
            if (isMounted) {
              if (statusData.status === 'success' || statusData.status === 'completed') {
                setPaymentStatus('success');
                setActiveStep(3);
                if (statusData.transactionId) {
                  setTransactionId(statusData.transactionId);
                }
              } else if (statusData.status === 'failed') {
                setPaymentStatus('failed');
                setActiveStep(3);
                setError(statusData.error || 'Payment failed');
              }
            }
          }
        } catch (storageErr) {
          console.warn('Error reading payment status from sessionStorage:', storageErr);
        }
        
        // Check sessionStorage for order details
        try {
          const storedOrder = sessionStorage.getItem(`order_${orderReference}`);
          if (storedOrder && isMounted) {
            const orderData = JSON.parse(storedOrder);
            console.log('Found order details in sessionStorage:', orderData);
            setOrderDetails(orderData);
          }
        } catch (storageErr) {
          console.warn('Error reading order from sessionStorage:', storageErr);
        }
        
        // Try to find the order in Firestore as backup
        if (isMounted) {
          const ordersRef = collection(db, 'orders');
          let orderDoc = null;
          
          // First try to find by Firestore ID
          try {
            orderDoc = await getDoc(doc(db, 'orders', orderReference));
          } catch (e) {
            console.log('Not a valid Firestore ID, searching by reference...');
          }
          
          // If not found, try to find by localOrderId field
          if (!orderDoc || !orderDoc.exists()) {
            try {
              const orderQuery = query(
                ordersRef, 
                where('localOrderId', '==', orderReference)
              );
              
              const querySnapshot = await getDocs(orderQuery);
              if (!querySnapshot.empty) {
                orderDoc = querySnapshot.docs[0];
              }
            } catch (queryErr) {
              console.error('Error querying by localOrderId:', queryErr);
            }
          }
          
          if (orderDoc && orderDoc.exists() && isMounted) {
            const data = orderDoc.data();
            setOrderDetails({
              id: orderDoc.id,
              ...data
            });
            
            // If order already has a payment status, use it
            if (data.paymentStatus === 'completed') {
              setPaymentStatus('success');
              setActiveStep(3);
            } else if (data.paymentStatus === 'failed') {
              setPaymentStatus('failed');
              setActiveStep(3);
              setError(data.paymentDetails?.error?.message || 'Payment failed');
            }
          }
        }
      } catch (err) {
        console.error('Error fetching order:', err);
      }
    };
    
    fetchOrderDetails();
    
    return () => {
      isMounted = false;
    };
  }, [orderReference]);
  
  // Poll payment status from the API with Firestore and sessionStorage tracking
  useEffect(() => {
    // Don't start polling if we don't have an order reference or if payment is already complete
    if (!orderReference) {
      setError('Missing order reference');
      return;
    }
    
    if (paymentStatus === 'success' || paymentStatus === 'failed') {
      console.log('Payment already has final status:', paymentStatus);
      return;
    }

    // Set a maximum number of polls to prevent infinite polling
    const MAX_POLLS = 30; // 30 polls * 5 seconds = 150 seconds max wait time
    const POLL_INTERVAL = 5000; // 5 seconds
    let timeoutId = null;
    let isMounted = true;
    
    const checkStatus = async () => {
      if (!isMounted) return;
      
      if (pollCount >= MAX_POLLS) {
        // Max polls reached, redirect to payment failure
        setPaymentStatus('timeout');
        setError('Payment status check timed out');
        setActiveStep(3);
        
        // Update Firestore with timeout status if we have a valid Firestore ID
        if (orderDetails && orderDetails.id && !orderDetails.isFirestoreFallback) {
          try {
            // Non-blocking update
            updateDoc(doc(db, 'orders', orderDetails.id), {
              paymentStatus: 'timeout',
              updatedAt: serverTimestamp(),
              paymentDetails: {
                ...orderDetails.paymentDetails || {},
                error: 'Payment status check timed out',
                lastChecked: new Date().toISOString()
              }
            }).catch(err => console.warn('Failed to update order with timeout status:', err));
          } catch (e) {
            console.warn('Error updating Firestore with timeout status:', e);
          }
        }
        return;
      }
      
      try {
        // First check sessionStorage for payment status updates
        try {
          const storedPaymentStatus = sessionStorage.getItem(`payment_status_${orderReference}`);
          if (storedPaymentStatus) {
            const statusData = JSON.parse(storedPaymentStatus);
            const statusTimestamp = new Date(statusData.timestamp).getTime();
            const currentTime = Date.now();
            const isRecent = (currentTime - statusTimestamp) < 300000; // 5 minutes
            
            if (isRecent) {
              console.log('Found recent payment status in sessionStorage:', statusData);
              
              if (statusData.status === 'success' || statusData.status === 'completed') {
                if (isMounted) {
                  setPaymentStatus('success');
                  setActiveStep(3);
                  // Update sessionStorage to mark as handled
                  sessionStorage.setItem(`payment_status_${orderReference}`, JSON.stringify({
                    ...statusData,
                    handled: true
                  }));
                  // Wait 2 seconds before redirecting to success page
                  timeoutId = setTimeout(() => {
                    if (isMounted) {
                      navigate(`/marketplace/payment-success?reference=${orderReference}${transactionId ? `&transaction_id=${transactionId}` : ''}`);
                    }
                  }, 2000);
                  return;
                }
              } else if (statusData.status === 'failed') {
                if (isMounted) {
                  setPaymentStatus('failed');
                  setActiveStep(3);
                  setError(statusData.error || 'Payment failed');
                  // Update sessionStorage to mark as handled
                  sessionStorage.setItem(`payment_status_${orderReference}`, JSON.stringify({
                    ...statusData,
                    handled: true
                  }));
                  // Wait 2 seconds before redirecting to failure page
                  timeoutId = setTimeout(() => {
                    if (isMounted) {
                      navigate(`/marketplace/payment-failure?reference=${orderReference}&error=${encodeURIComponent(statusData.error || 'Payment failed')}&error_code=${statusData.errorCode || 'unknown'}`);
                    }
                  }, 2000);
                  return;
                }
              }
            }
          }
        } catch (storageErr) {
          console.warn('Error reading payment status from sessionStorage:', storageErr);
        }
        
        if (!isMounted) return;
        setActiveStep(2);
        
        // Use the order reference to check payment status
        // If we have a Firestore ID in orderDetails, prioritize using that
        const referenceToUse = (orderDetails && orderDetails.firestoreId) ? orderDetails.firestoreId : orderReference;
        
        // Extract seller ID from order details if available
        let sellerId = null;
        if (orderDetails && orderDetails.items && orderDetails.items.length > 0) {
          // Extract unique seller IDs from order items
          const uniqueSellers = new Set();
          orderDetails.items.forEach(item => {
            if (item.sellerId) {
              uniqueSellers.add(item.sellerId);
            }
          });
          
          // If there's at least one seller ID, use the first one
          if (uniqueSellers.size > 0) {
            sellerId = Array.from(uniqueSellers)[0];
            console.log(`Using seller ID for payment status check: ${sellerId}`);
          }
        }
        
        // Pass the seller ID to the checkPaymentStatus function
        const result = await checkPaymentStatus(referenceToUse, sellerId);
        console.log('Payment status check result for reference', referenceToUse, ':', result);
        
        // Payment is being processed automatically
        if (result.status === 'pending') {
          // Update UI to show processing
          if (isMounted) {
            setPaymentStatus('processing');
            setActiveStep(2);
            
            // If this is the first time we're seeing this status, show a message
            if (!sessionStorage.getItem(`processing_shown_${referenceToUse}`)) {
              sessionStorage.setItem(`processing_shown_${referenceToUse}`, 'true');
              console.log('PAYMENT IS BEING PROCESSED: Please check your phone for PIN prompt');
            }
          }
        }
        // Handle completed payments
        else if (result.status === 'completed' || result.status === 'success') {
          if (isMounted) {
            setPaymentStatus('success');
            setActiveStep(3);
            
            // Update sessionStorage to mark as handled
            sessionStorage.setItem(`payment_status_${orderReference}`, JSON.stringify({
              ...result,
              handled: true
            }));
            
            // Wait 2 seconds before redirecting to success page
            timeoutId = setTimeout(() => {
              if (isMounted) {
                navigate(`/marketplace/payment-success?reference=${orderReference}${result.transactionId ? `&transaction_id=${result.transactionId}` : ''}`);
              }
            }, 2000);
          }
        }
        // Handle failed payments
        else if (result.status === 'failed') {
          if (isMounted) {
            setPaymentStatus('failed');
            setActiveStep(3);
            setError(result.error || 'Payment failed');
            
            // Update sessionStorage to mark as handled
            sessionStorage.setItem(`payment_status_${orderReference}`, JSON.stringify({
              ...result,
              handled: true
            }));
            
            // Wait 2 seconds before redirecting to failure page
            timeoutId = setTimeout(() => {
              if (isMounted) {
                navigate(`/marketplace/payment-failure?reference=${orderReference}&error=${encodeURIComponent(result.error || 'Payment failed')}&error_code=${result.errorCode || 'unknown'}`);
              }
            }, 2000);
          }
        }
        // For all other statuses, just update the UI
        else {
          if (isMounted) {
            setPaymentStatus('processing');
            setProgress(result.progress || 0);
          }
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        
        if (!isMounted) return;
        
        // In development mode, handle errors more gracefully
        if (process.env.NODE_ENV === 'development') {
          console.log('Development mode: Continuing despite error');
          setPollCount(prev => prev + 1);
          timeoutId = setTimeout(checkStatus, POLL_INTERVAL);
          return;
        }
        
        // On error, still poll again until max polls reached
        setPollCount(prev => prev + 1);
        timeoutId = setTimeout(checkStatus, POLL_INTERVAL);
      }
    };
    
    // Start polling after a short delay
    timeoutId = setTimeout(checkStatus, 2000);
    
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [orderReference, paymentStatus, pollCount, navigate, transactionId, orderDetails]);
  
  // Payment status icons for reference
  // eslint-disable-next-line no-unused-vars
  const statusIcons = {
    created: <Receipt />,
    awaiting: <Payment />,
    processing: <Sync />,
    completed: <CheckCircle />,
    failed: <Error />
  };
  
  const getStatusMessage = () => {
    switch (paymentStatus) {
      case 'success':
        return 'Payment successful! Your order has been confirmed.';
      case 'failed':
        return `Payment failed: ${error || 'An error occurred during payment processing'}`;
      case 'waiting_confirmation':
        return 'Please check your phone for the PIN prompt and enter your PIN to confirm payment. This may take a few moments to arrive.';
      case 'pending':
        return 'Payment is being processed. Please wait while we verify your payment...';
      case 'processing':
        return activeStep === 1 
          ? 'Please check your phone for a PIN prompt from your mobile money provider. This may take up to 2 minutes...'
          : 'Your payment is being processed. Please wait while we confirm the transaction...';
      case 'timeout':
        return 'Payment status check timed out. If you completed the payment on your phone, please check your order status in your account.';
      default:
        return 'Checking payment status. Please be patient...';
    }
  };
  
  const handleManualRedirect = () => {
    if (paymentStatus === 'success') {
      navigate(`/marketplace/payment-success?reference=${orderReference}${transactionId ? `&transaction_id=${transactionId}` : ''}`);
    } else {
      navigate(`/marketplace/payment-failure?reference=${orderReference}&error=${encodeURIComponent(error || 'Payment failed')}&error_code=manual_redirect`);
    }
  };
  
  // Handle notification close
  const handleNotificationClose = () => {
    // This function is passed to the PaymentStatusDisplay component
    // to handle closing notifications
  };
  
  // Map our payment status to the format expected by PaymentStatusDisplay
  const mapPaymentStatus = () => {
    switch(paymentStatus) {
      case 'processing':
        return activeStep === 1 ? 'initiated' : 'processing';
      case 'success':
        return 'completed';
      case 'failed':
        return 'failed';
      case 'timeout':
        return 'failed';
      case 'waiting_confirmation':
        return 'waiting_confirmation';
      default:
        return 'processing';
    }
  };
  
  // Create error object if we have an error
  const errorObject = error ? { message: error } : null;
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 2,
            backgroundColor: 'rgba(30, 30, 35, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            color: 'white'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Processing Your Payment
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {activeStep === 1 
                ? 'Please check your phone for a mobile money prompt'
                : 'Please wait while we confirm your payment'}
            </Typography>
            {activeStep === 1 && (
              <Alert 
                severity="info" 
                sx={{ 
                  mt: 2, 
                  textAlign: 'left',
                  backgroundColor: 'rgba(33, 150, 243, 0.15)',
                  color: '#90caf9',
                  '& .MuiAlert-icon': {
                    color: '#90caf9'
                  }
                }}
              >
                <Typography variant="body2">
                  <strong>Important:</strong> You should receive a prompt on your mobile phone to enter your PIN. 
                  Please complete this step to proceed with the payment.
                </Typography>
              </Alert>
            )}
          </Box>
          
          <Divider sx={{ my: 3, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          {/* Payment Status Display Component */}
          <PaymentStatusDisplay 
            status={mapPaymentStatus()}
            message={getStatusMessage()}
            error={errorObject}
            isLoading={paymentStatus === 'processing'}
            transactionId={transactionId}
            onClose={handleNotificationClose}
            progress={progress} // Pass progress to the component
          />
          
          {/* Status icon is now handled by PaymentStatusDisplay component */}
          
          {/* Automatic payment processing - no manual confirmation needed */}
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
            {(paymentStatus === 'success' || paymentStatus === 'failed' || paymentStatus === 'timeout') && (
              <Button 
                variant="contained" 
                color={paymentStatus === 'success' ? 'primary' : 'error'}
                onClick={handleManualRedirect}
                startIcon={paymentStatus === 'success' ? <CheckCircle /> : <Error />}
                sx={{ 
                  borderRadius: '12px',
                  textTransform: 'none',
                  padding: '10px 24px',
                  fontWeight: 600
                }}
              >
                {paymentStatus === 'success' 
                  ? 'View Order Details' 
                  : 'Return to Marketplace'}
              </Button>
            )}
            
            <Button
              variant="outlined"
              onClick={() => navigate('/marketplace')}
              startIcon={<Home />}
              sx={{ 
                borderRadius: '12px',
                textTransform: 'none',
                padding: '10px 24px',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'white',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Return to Marketplace
            </Button>
          </Box>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default PaymentProcessing;
